/**
 * Trida pro pocitani sirky Align Panelu.
 * Sirku pocita z definice posilane ze serveru.
 */
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";

export class AlignPanelWidthCounter {
    /** Ziska prvni align panel v elementu (muze byt i samotny element) */
    public static getFirstAlignPanel(el: HTMLElement): HTMLElement {
        return helper.getAttr(el, "tsccontrol") === "alignpanel" ? el : el.querySelector("[data-TSCControl=\"AlignPanel\"]") as HTMLElement;
    }

    /** Spocita min. sirku Align Panelu */
    public static countAlignPanel(div: HTMLElement): number {
        if (AlignPanelWidthCounter.isPositionedAp(div))
            return AlignPanelWidthCounter.countPositionedAp(div);
        else
            return AlignPanelWidthCounter.countDockedAp(div);
    }

    /** Spocita minimalni sirku dokovaneho Align Panelu */
    public static countDockedAp(div: HTMLElement): number {
        const lefts: number[] = [];
        const rights: number[] = [];
        let client: number = 0;
        const others: number[] = [];
        const widths: number[] = [];

        for (let i = 0; i < div.children.length; i++) {
            const child = div.children[i] as HTMLElement;
            let align = helper.getAttr(child, "align");
            align = align != null ? align.toLocaleLowerCase() : align;
            if (align == "alleft") {
                lefts.push(helper.getNumAttr(child, "width"));
                widths.push(helper.getNumAttr(child, "width"));
            } else if (align == "alright") {
                rights.push(helper.getNumAttr(child, "width"));
                widths.push(helper.getNumAttr(child, "width"));
            } else {
                const w = AlignPanelWidthCounter.countDockApSubcomponent(child);
                others.push(w);
                widths.push(w);

                if (align == "alclient")
                    client = w;
            }
        }

        //helper.log('dokovany Align Panel', div, lefts, rights, client, others,
        //lefts.concat(rights, others).reduce((total, num) => { return total + num }, 0));
        return AlignPanelWidthCounter.countMinWidthOfDockedAp(div, widths, lefts, rights, client);
    }

    /** Zkombinuje sirky komponent dokovaneho Align Panelu a vrati minimalni potrebnou sirku */
    public static countMinWidthOfDockedAp(div: HTMLElement, widths: number[], lefts: number[], rights: number[], client: number): number {
        let firstLeftIndex: number;
        const topsInsideLefts: number[] = []; // indexy top/bottom uvnitr left/right

        for (let i = 0; i < div.children.length; i++) {
            const child = div.children[i] as HTMLElement;
            let align = helper.getAttr(child, "align");
            align = align != null ? align.toLocaleLowerCase() : align;
            if ((align == "alleft" || align == "alright" || align == "alclient") && isNaN(firstLeftIndex)) {
                firstLeftIndex = i;
            } else if ((align == "altop" || align == "albottom") && isFinite(firstLeftIndex)) { // indexy alTop/alBottom uvnitr alLeft/alRight
                topsInsideLefts.push(i);
            }
        }

        if (isNaN(firstLeftIndex))
            firstLeftIndex = widths.length;

        // soucet vsech alLeft + alRight + alClient = jedna moznost minimalni sirky panelu
        const leftsRightsClientWidth = lefts.concat(rights).reduce((prev, cur) => {
            return prev + cur;
        }, 0) + client;
        // maximum ze vsech alTop/alBottom, ktere jsou pred alLeft/alRight = druha moznost minimalni sirky panelu
        const topsBeforeLeftsMaxWidth = firstLeftIndex > 0 ? Math.max.apply(null, widths.filter((_val, index) => {
            return index < firstLeftIndex;
        })) : 0;

        const topsWidths: number[] = [];

        if (topsInsideLefts.length) {
            topsInsideLefts.forEach((val) => { // pro kazdy top/bottom uvnitr left/right se secte jeho sirka + soucet vsech left a right pred nim
                let width: number = widths[val];
                for (let i = val - 1; i >= firstLeftIndex; i--) {
                    let align = helper.getAttr(div.children[i], "align");
                    align = align != null ? align.toLocaleLowerCase() : align;
                    if (align == "alleft" || align == "alright")
                        width += widths[i];
                }
                topsWidths.push(width);
            });
        }

        // topy/bottomy za (uvnitr) left/right = treti moznost minimalni sirky panelu
        const topsInsideLeftsMaxWidth = topsInsideLefts.length ? Math.max.apply(null, topsWidths) : 0;

        //helper.log('minimum z dokovaneho panelu', div, topsBeforeLeftsMaxWidth, leftsRightsClientWidth, topsInsideLeftsMaxWidth);
        return Math.max(topsBeforeLeftsMaxWidth, leftsRightsClientWidth, topsInsideLeftsMaxWidth);
    }

    /** Spocita min. sirku top/bottom/client komponenty pozicovaneho Align Panelu */
    public static countDockApSubcomponent(div: HTMLElement): number {
        let width = 0;
        const type = helper.getAttr(div, "tsccontrol");

        switch (type) {
            case "wrappanel":
                width = this.countWrapPanel(div);
                break;
            case "alignpanel":
                width = this.countAlignPanel(div);
                break;
            case "verticalscrollviewer":
                width = this.countVerticalScrollViewer(div);
                break;
        }

        // helper.log('top|bottom|client', div, width);
        return width;
    }

    /** Spocita minimalni sirku childu */
    public static countMinWidthOfChildren(div: HTMLElement): number {
        const widths: number[] = [];

        for (let i = 0; i < div.children.length; i++) {
            const type = helper.getAttr(div.children[i], "tsccontrol");

            switch (type) {
                case "wrappanel":
                    widths[i] = this.countWrapPanel(div.children[i] as HTMLElement);
                    break;
                case "alignpanel":
                    widths[i] = this.countAlignPanel(div.children[i] as HTMLElement);
                    break;
                case "verticalscrollviewer":
                    widths[i] = this.countVerticalScrollViewer(div.children[i] as HTMLElement);
                    break;
            }
        }

        //helper.log('rekurze', div, widths, maxWidth);
        return Math.max.apply(null, widths);
    }

    /** Spocita minimalni potrebnou sirku pro zobrazeni Vertical Scroll Vieweru */
    public static countVerticalScrollViewer(scroll: HTMLElement): number {
        const parent = scroll.parentElement;
        const vsvWidth = helper.getNumAttr(scroll, "width");
        let width = 0;

        if (helper.getAttr(parent, "tsccontrol") == "wrappanel")
            width = vsvWidth;
        else // parent je AlignPanel
        if (AlignPanelWidthCounter.isPositionedAp(parent))
            width = vsvWidth;
        else // parent je dokovany AP
        if (helper.getAttr(scroll, "align").toLocaleLowerCase() == "alleft" || helper.getAttr(scroll, "align").toLocaleLowerCase() == "alright")
            width = vsvWidth;
        else
            width = this.countMinWidthOfChildren(scroll);

        //helper.log('vertical', scroll, width);
        return width;
    }

    /** Spocita minimalni potrebnou sirku Wrap Panelu (jak horizontal tak vertikal wrap) */
    public static countWrapPanel(div: HTMLElement): number {
        const widths = helper.toArray<HTMLElement>(div.children).map((child) => {
            return helper.getNumAttr(child, "width");
        });

        // helper.log('wrap', div, widths, Math.max.apply(null, widths));
        return Math.max.apply(null, widths);
    }

    /** Jestli je Align Panel pozicovany */
    public static isPositionedAp(div: HTMLElement): boolean {
        const e = div.firstElementChild;
        if (e) {
            if (!helper.getAttr(e, "align") || helper.getAttr(e, "align").toLocaleLowerCase() == "alnone")
                return true;
            else
                return false;
        }
        return true;
    }

    /** Jestli je primo pod Align Panelem ListFrame */
    public static isListFrameParent(div: HTMLElement): boolean {
        return helper.toArray<HTMLElement>(div.children).some((child) => {
            return helper.getAttr(child, "tsccontrol") === "listframe";
        });
    }

    /** Spocita min. sirku pozicovaneho Align Panelu */
    public static countPositionedAp(div: HTMLElement): number {
        const widths = helper.toArray<HTMLElement>(div.children).map((child) => {
            return helper.getNumAttr(child, "width") + helper.getNumAttr(child, "left");
        });

        //helper.log('pozicovany AP', div, maxWidth);
        return Math.max.apply(null, widths);
    }

    /** Spocita min. VYSKU pozicovaneho Align Panelu */
    public static countPositionedApHeight(div: HTMLElement): number {
        const heights = helper.toArray<HTMLElement>(div.children).map((child) => {
            return helper.getNumAttr(child, "height") + helper.getNumAttr(child, "top");
        });

        //helper.log('pozicovany AP vyska', div, Math.max.apply(null, widths));
        return Math.max.apply(null, heights);
    }
}

const logger = LazyLoadingSectionLogger.create(LoggingSection.alignPanel);

export class helper {
    /** Prevede cokoliv indexovatelneho na Array, aby se na tom mohly pouzivat Array metody (pouzitelne pro HTMLCollection, NodeList, ...) */
    public static toArray<T>(coll: Indexable): T[] {
        return Array.prototype.slice.call(coll) as T[];
    }

    /** Nacita hodnotu data atributu, vraci ji jako lower case string, orezany. Pokud atribut neexistuje, vraci null. */
    public static getAttr(element: Element, attr: string): string {
        const val = element.getAttribute("data-" + attr);
        return val == null ? val : val.trim().toLowerCase();
    }

    /** Vraci hodnotu data atributu jako cislo; pokud atribut neexistuje, je prazdny nebo NaN, vraci null */
    public static getNumAttr(element: Element, attr: string): number {
        const val = this.getAttr(element, attr);
        if (val == null || val == "" || isNaN(+val))
            return null;
        return +val;
    }

    /** logovaci funkce */
    public static log(title: string, ...args: any[]): void {
        logger.log("START " + title + "_________________");
        args.forEach((val) => {
            logger.log(val);
        });
        logger.log("END " + title + "*****************");
    }
}

export interface Indexable {
    length: number;
    [index: number]: any;
}
